import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Select, DatePicker, Space, message, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../services/api';

const { RangePicker } = DatePicker;

const Campaigns = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchCampaigns();
    fetchBrands();
  }, []);

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      const response = await api.get('/campaigns');
      setCampaigns(response.data);
    } catch (error) {
      message.error('Kampanyalar yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const fetchBrands = async () => {
    try {
      const response = await api.get('/brands');
      setBrands(response.data);
    } catch (error) {
      message.error('Markalar yüklenirken bir hata oluştu');
    }
  };

  const handleModalOk = () => {
    form.submit();
  };

  const handleModalCancel = () => {
    setModalVisible(false);
    setEditingCampaign(null);
    form.resetFields();
  };

  const handleSubmit = async (values) => {
    try {
      const data = {
        ...values,
        startDate: values.dateRange[0].format('YYYY-MM-DD'),
        endDate: values.dateRange[1].format('YYYY-MM-DD')
      };
      delete data.dateRange;

      if (editingCampaign) {
        await api.put(`/campaigns/${editingCampaign.id}`, data);
        message.success('Kampanya güncellendi');
      } else {
        await api.post('/campaigns', data);
        message.success('Kampanya eklendi');
      }
      handleModalCancel();
      fetchCampaigns();
    } catch (error) {
      message.error(error.response?.data?.error || 'Bir hata oluştu');
    }
  };

  const handleEdit = (record) => {
    setEditingCampaign(record);
    form.setFieldsValue({
      name: record.name,
      brandId: record.Brand.id,
      dateRange: [dayjs(record.startDate), dayjs(record.endDate)]
    });
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/campaigns/${id}`);
      message.success('Kampanya silindi');
      fetchCampaigns();
    } catch (error) {
      message.error(error.response?.data?.error || 'Kampanya silinirken bir hata oluştu');
    }
  };

  const columns = [
    {
      title: 'Kampanya Adı',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name)
    },
    {
      title: 'Marka',
      dataIndex: 'Brand',
      key: 'brand',
      render: (brand) => brand.name
    },
    {
      title: 'Başlangıç Tarihi',
      dataIndex: 'startDate',
      key: 'startDate',
      render: (text) => dayjs(text).format('DD.MM.YYYY'),
      sorter: (a, b) => dayjs(a.startDate).valueOf() - dayjs(b.startDate).valueOf()
    },
    {
      title: 'Bitiş Tarihi',
      dataIndex: 'endDate',
      key: 'endDate',
      render: (text) => dayjs(text).format('DD.MM.YYYY')
    },
    {
      title: 'Durum',
      key: 'status',
      render: (_, record) => {
        const now = dayjs();
        const start = dayjs(record.startDate);
        const end = dayjs(record.endDate);

        if (now.isBefore(start)) {
          return <span style={{ color: 'orange' }}>Başlayacak</span>;
        } else if (now.isAfter(end)) {
          return <span style={{ color: 'red' }}>Sona Erdi</span>;
        } else {
          return <span style={{ color: 'green' }}>Devam Ediyor</span>;
        }
      }
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Düzenle
          </Button>
          <Popconfirm
            title="Kampanyayı silmek istediğinizden emin misiniz?"
            onConfirm={() => handleDelete(record.id)}
            okText="Evet"
            cancelText="Hayır"
          >
            <Button type="primary" danger icon={<DeleteOutlined />}>
              Sil
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            setEditingCampaign(null);
            form.resetFields();
            setModalVisible(true);
          }}
        >
          Yeni Kampanya Ekle
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={campaigns}
        rowKey="id"
        loading={loading}
      />

      <Modal
        title={editingCampaign ? 'Kampanya Düzenle' : 'Yeni Kampanya Ekle'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="name"
            label="Kampanya Adı"
            rules={[
              { required: true, message: 'Lütfen kampanya adını girin' },
              { max: 200, message: 'Kampanya adı en fazla 200 karakter olabilir' }
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="brandId"
            label="Marka"
            rules={[{ required: true, message: 'Lütfen marka seçin' }]}
          >
            <Select placeholder="Marka seçin">
              {brands.map(brand => (
                <Select.Option key={brand.id} value={brand.id}>
                  {brand.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="dateRange"
            label="Kampanya Tarihleri"
            rules={[{ required: true, message: 'Lütfen kampanya tarihlerini seçin' }]}
          >
            <RangePicker style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Campaigns; 